import React, { useEffect, useMemo, WheelEvent } from "react";
import MaskInput from "lib/maskInput";
import {
  DescriptionWrap,
  IconWrap,
  InputEl,
  InputFieldErrorEl,
  InputLabelEl,
  InputProps,
  InputTextEl,
  InputTextFieldEl,
  InputWrap
} from "atom/input/inputComponents";
import IMask from "imask";
import { ErrorIcon, XCrossIcon } from "constants/Icon";
import { EyeIcon } from "../../../icons/EyeIcon";
import { EyeClosedIcon } from "../../../icons/EyeClosedIcon";

/**
 * Strips invisible Unicode control characters from pasted text.
 *
 * This prevents issues with phone numbers copied from services like Google Voice
 * that include directionality marks (LTR/RTL) and formatting controls.
 * These hidden characters break validation and form submission logic.
 *
 * Handles: U+200B-U+200F (zero-width spaces), U+202A-U+202E (directionality),
 * U+2066-U+2069 (isolates), and C0/C1 control characters.
 */
function stripUnicodeControlChars(str: string): string {
  // Use RegExp constructor to avoid linter complaints about control chars in literals
  const controlCharPattern =
    "[\\u0000-\\u001F\\u007F-\\u009F\\u200B-\\u200F\\u202A-\\u202E\\u2066-\\u2069]";
  return str.replace(new RegExp(controlCharPattern, "gu"), "");
}

function Input(props: InputProps) {
  const [value, setValue] = React.useState(props.defaultValue ?? "");
  const {
    label,
    mask,
    onChangeCapture,
    onMaskChange,
    error,
    description,
    DescriptionIcon,
    icon,
    errorParser = ({ error }) => {
      if (!error) {
        return "";
      }
      if (typeof error === "string") {
        return error;
      }
      return error.message;
    },
    ...textFieldProps
  } = props;
  const elRef = React.useRef<HTMLInputElement>(null);
  // Prevents race conditions between paste handler and useEffect onChange calls
  const isPasteHandlingRef = React.useRef(false);

  const isPasswordToggleEnabled =
    props.type === "password" && props.isPasswordToggleEnabled;

  const [seePassword, setSeePassword] = React.useState(false);

  const handlePasswordToggleClick = () => {
    setSeePassword(!seePassword);
  };

  const maskInstanceRef = React.useRef<MaskInput | null>(null);

  useEffect(() => {
    if (!elRef.current || !mask) {
      return;
    }
    const instance = new MaskInput(elRef.current, mask);
    maskInstanceRef.current = instance;

    const handleAccept = () => {
      if (isPasteHandlingRef.current) {
        return;
      }
      const { maskInstance } = instance;
      const maskedValue = maskInstance?.value ?? "";
      setValue(maskedValue);
      textFieldProps.onChange?.(maskedValue);

      if (onMaskChange && maskInstance) {
        onMaskChange(maskInstance);
      }
    };

    instance.maskInstance?.on("accept", handleAccept);

    return () => {
      instance.maskInstance?.off("accept", handleAccept);
      instance.destroy();
      maskInstanceRef.current = null;
    };
  }, [elRef.current, mask]);

  React.useImperativeHandle(
    props.ref,
    () => ({
      setValue
    }),
    [elRef]
  );

  const pipedValue = useMemo(() => {
    if (!mask) {
      return value;
    }
    return IMask.pipe(value, mask);
  }, [mask, value]);

  useEffect(() => {
    // When no mask, call onChange directly on value changes
    if (mask) {
      return;
    }
    textFieldProps.onChange?.(value);
  }, [value, mask]);

  const errorString = errorParser(props);

  const handlePasteCapture = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const clipboardData = e.clipboardData;
    if (clipboardData) {
      try {
        let value = clipboardData.getData("text");
        // Remove invisible Unicode control characters that break validation
        value = stripUnicodeControlChars(value.trim());
        if (mask && elRef.current) {
          // Prevent native paste + IMask default handling to avoid double-processing
          e.preventDefault();
          e.stopPropagation();

          isPasteHandlingRef.current = true;
          const piped = IMask.pipe(value, mask);
          setValue(piped);

          // Delay onChange calls to prevent race conditions with useEffect
          // This ensures the sanitized value is used for validation
          setTimeout(() => {
            // Trigger form validation with the sanitized value
            textFieldProps.onChange?.(piped);

            // Trigger onMaskChange with mask instance
            const maskInstance = maskInstanceRef.current?.maskInstance;
            if (onMaskChange && maskInstance) {
              onMaskChange(maskInstance);
            }

            // Also trigger any capture phase handlers
            if (onChangeCapture) {
              const syntheticEvent = {
                target: elRef.current,
                currentTarget: elRef.current,
                type: "change",
                bubbles: true,
                cancelable: true,
                preventDefault: () => {},
                stopPropagation: () => {}
              } as React.ChangeEvent<HTMLInputElement>;

              onChangeCapture(syntheticEvent);
            }

            isPasteHandlingRef.current = false;
          }, 0);
        }
      } catch (e) {
        console.warn("Can't access clipboard text", e);
        isPasteHandlingRef.current = false;
      }
    }
  };

  const numberInputOnWheelPreventChange = (e: WheelEvent<HTMLInputElement>) => {
    // if input not focused, do nothing
    if (document.activeElement !== e.currentTarget) {
      return;
    }

    const eventTarget = e.target as HTMLInputElement;

    eventTarget.blur();

    // Prevent the page/container scrolling
    e.stopPropagation();

    // Refocus immediately, on the next tick (after the current function is done)
    setTimeout(() => {
      eventTarget.focus();
    }, 0);
  };

  return (
    <InputTextFieldEl
      {...textFieldProps}
      type={seePassword ? "text" : props.type}
      value={pipedValue}
      onChange={(e) => {
        setValue(e);
      }}
      ref={null}
      className="input"
      isInvalid={Boolean(error)}
    >
      <InputWrap className="input-wrap">
        <InputEl
          onWheel={numberInputOnWheelPreventChange}
          value={pipedValue}
          ref={elRef}
          onChangeCapture={onChangeCapture}
          onPasteCapture={handlePasteCapture}
        />
        {label && <InputLabelEl className="body1">{label}</InputLabelEl>}

        <IconWrap>
          {icon ? (
            <div>{icon}</div>
          ) : (
            <>
              {Boolean(error) && props.type !== "password" && <ErrorIcon />}

              {props.clearable && !error && (
                <div
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    setValue("");
                    elRef.current?.focus();
                  }}
                >
                  <XCrossIcon />
                </div>
              )}

              {isPasswordToggleEnabled && (
                <div onClick={handlePasswordToggleClick}>
                  {seePassword ? <EyeClosedIcon /> : <EyeIcon />}
                </div>
              )}
            </>
          )}
        </IconWrap>
      </InputWrap>
      {description && (
        <DescriptionWrap>
          {DescriptionIcon && (
            <div style={{ marginTop: "0.5rem" }}>
              <DescriptionIcon />
            </div>
          )}

          <InputTextEl slot="description" className="little1">
            {description}
          </InputTextEl>
        </DescriptionWrap>
      )}
      <InputFieldErrorEl className={"little1 data-field-error"}>
        {errorString}
      </InputFieldErrorEl>
    </InputTextFieldEl>
  );
}

export default Input;
